"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function BakerySales({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm amber backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#fbbf24]/5 rounded-full blur-[120px] -z-10 pointer-events-none" />
      
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Centered Yellow Cake Icon & Heading */}
        <div className="text-center flex flex-col items-center mb-12">
          {section.icon && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -20 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -10 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, type: "spring" }}
              className="mb-4 rotate-350 mx-auto"
              style={{ width: "80px", height: "80px", transform: "rotate(-10deg)" }}
            >
              <Image
                src={section.icon}
                alt=""
                width={80}
                height={80}
                className="w-full h-full object-contain"
              />
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white text-center leading-tight mb-5 max-w-4xl"
          >
            Recipe for success: <br />
            <span className="x_wd_yellow_highlight_bold_03 mt-2 inline-block">
              all-in-one
            </span>{" "}
            PoS
          </motion.h2>
        </div>

        {/* annotated image screenshot block */}
        <div className="relative max-w-[850px] mx-auto mb-16 px-4 lg:px-0">
          
          {/* s_wd_screenshot_arrows */}
          {/* Customized selling */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_left" style={{ bottom: "65%" }}>
            <h3 className={`${caveat.className} mb-0`}>Customized selling</h3>
            <Image
              src="/odoo/arrows/grey_arrow_sm_03.svg"
              width={20}
              height={20}
              className="rotate-300"
              alt=""
            />
          </div>

          {/* Loyalty programs */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_left" style={{ bottom: "45%" }}>
            <h3 className={`${caveat.className} mb-0`}>Loyalty programs</h3>
            <Image
              src="/odoo/arrows/grey_arrow_sm_02.svg"
              width={20}
              height={20}
              className="rotate-320"
              alt=""
            />
          </div>

          {/* Multiple payment methods */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_left" style={{ bottom: "7%" }}>
            <h3 className={`${caveat.className} mb-0`}>Multiple payment methods</h3>
            <Image
              src="/odoo/arrows/grey_arrow_sm_03.svg"
              width={20}
              height={20}
              className="rotate-300"
              alt=""
            />
          </div>

          {/* Reliable online and offline */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_right" style={{ bottom: "92%" }}>
            <h3 className={`${caveat.className} mb-0`}>Reliable online and offline</h3>
            <Image
              src="/odoo/arrows/grey_arrow_sm_03.svg"
              width={20}
              height={20}
              className="rotate-60"
              alt=""
            />
          </div>

          {/* Filter by product categories */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_right" style={{ top: "15%" }}>
            <Image
              src="/odoo/arrows/grey_arrow_sm_02.svg"
              width={20}
              height={20}
              className="rotate-140"
              alt=""
            />
            <h3 className={`${caveat.className} mb-0`}>Filter by product categories</h3>
          </div>

          {/* Intuitive display */}
          <div className="s_wd_screenshot_arrow s_wd_screenshot_arrow_right" style={{ top: "70%" }}>
            <Image
              src="/odoo/arrows/grey_arrow_sm_03.svg"
              width={20}
              height={20}
              className="rotate-130"
              alt=""
            />
            <h3 className={`${caveat.className} mb-0`}>Intuitive display</h3>
          </div>

          {/* Main Mockup Tablet Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-all duration-300 z-10"
          >
            <Image
              src={section.image}
              alt="Bakery POS Shop Sales"
              width={850}
              height={530}
              className="w-full h-auto rounded-[18px] object-cover"
            />
          </motion.div>

        </div>

        {/* Bottom two descriptions */}
        <div className="flex flex-col md:flex-row justify-center gap-8 lg:gap-12 max-w-[1000px] mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="flex-1"
          >
            <p className="leading-relaxed mb-4 text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text1 }} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="flex-1"
          >
            <p className="leading-relaxed mb-0 text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text2 }} />
          </motion.div>
        </div>

      </div>
    </section>
  );
}

export function BakeryOrders({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm red backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rose-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Title and Subtitle Block */}
        <div className="grid lg:grid-cols-12 gap-6 mb-12">
          <div className="lg:col-span-10 lg:col-start-2">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-5`}
            >
              <span className="x_wd_red_highlight_01 mr-2 inline-block">
                {section.highlightText || "Intuitive"}
              </span>{" "}
              {section.title || "website builder"}
            </motion.h2>
          </div>
          
          <div className="lg:col-span-8 lg:col-start-2 max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p 
                className="leading-relaxed text-gray-300 mb-0 font-medium text-xl" 
                dangerouslySetInnerHTML={{ __html: section.text }} 
              />
            </motion.div>
          </div>
        </div>

        {/* Desktop Website Screenshot + Mobile Custom Order Form Mockup */}
        <div className="grid grid-cols-12 gap-8 items-center justify-center max-w-[1150px] mx-auto py-8">
          
          {/* Left/Middle: Full desktop screenshot mockup */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-span-12 md:col-span-8 lg:col-span-9 relative"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative z-10">
              <Image
                src={section.images[0]}
                alt="Bakery Website Mockup Desktop"
                width={850}
                height={530}
                className="w-full h-auto rounded-[14px] object-cover"
              />
            </div>
          </motion.div>

          {/* Right: Mobile Phone customized mockup with floating basket */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="col-span-8 col-start-3 sm:col-span-6 sm:col-start-4 md:col-span-4 lg:col-span-3 relative mt-4 md:mt-0"
          >
            {/* Peeking Floating Red Basket Icon */}
            {section.basketIcon && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5, rotate: -15, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 10, y: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 80, delay: 0.4 }}
                className="hidden md:block absolute bottom-[-20px] right-[-30px] z-20 pointer-events-none select-none"
                style={{ width: "100px", height: "100px" }}
              >
                <Image
                  src={section.basketIcon}
                  alt=""
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </motion.div>
            )}
            
            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.02] transition-all duration-300 relative z-10">
              <Image
                src={section.images[1]}
                alt="Bakery Custom Order mobile view"
                width={250}
                height={500}
                className="w-full h-auto rounded-[18px] object-cover"
              />
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function BakeryReplenishment({ section }) {
  return (
    <section className="pt-3 pt-lg-8 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm mint backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container">
        <div className="row g-5 align-items-center justify-between">
          
          {/* Mockup Column: Curved Background Shape + Green Warehouse Overlay */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-6 x_wd_shape x_wd_shape_re x_wd_shape_or py-5 order-2 order-lg-1 position-relative"
          >
            {/* White/Grey Dome Shape Backdrop Bubble */}
            <div className="absolute top-0 bottom-0 left-[-20%] right-[5%] bg-white/[0.03] rounded-r-[220px] -z-10 pointer-events-none" />
            
            {section.warehouseIcon && (
              <div className="d-none d-lg-inline position-absolute bottom-0 start-50 translate-middle-x rotate-350 ms-10 z-20 pointer-events-none select-none" style={{ width: "115px", height: "115px" }}>
                <Image
                  src={section.warehouseIcon}
                  width={115}
                  height={115}
                  className="w-full h-full object-contain"
                  alt=""
                />
              </div>
            )}
            <Image
              src={section.image}
              width={500}
              height={400}
              className="img-thumbnail w-lg-100 ms-auto relative z-10"
              alt="Bakery Purchase Orders replenishment"
            />
          </motion.div>

          {/* Copywriting Column: Headline + Cursive Highlight + Spaced Text */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="col-lg-5 offset-lg-1 order-1 order-lg-2 py-lg-6"
          >
            <h2 className={`${caveat.className} display-2 mb-5 text-white leading-tight`}>
              <span className="x_wd_green_highlight_bold_02 position-relative inline-block mr-2">
                {section.highlightText || "Optimal"}
              </span>{" "}
              {section.title || "stock at all times"}
            </h2>

            <div className="space-y-6">
              <p className="leading-relaxed mb-4 text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed mb-0 text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text2 }} />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function BakeryCommunication({ section }) {
  const [isPlaying, setIsPlaying] = React.useState(false);

  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-center">
      {/* Warm blue backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1000px] relative z-10">
        
        {/* Title and Description */}
        <div className="flex flex-col items-center mb-12 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6`}
          >
            <span className="x_wd_blue_highlight_05 mx-2 inline-block">
              {section.highlightText || "Streamlined"}
            </span>{" "}
            {section.title || "communication"}
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="leading-relaxed text-gray-300 font-medium text-xl"
            dangerouslySetInnerHTML={{ __html: section.text }}
          />
        </div>

        {/* Video Thumbnail Frame with Corner Graphic */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-[750px] mx-auto z-10 px-4 md:px-0"
        >
          {/* Peeking Hand-drawn lines_up_right splat graphic */}
          <div className="absolute -top-6 -right-6 w-16 h-16 md:w-20 md:h-20 pointer-events-none select-none z-20">
            <Image
              src="/odoo/graphics/lines_up_right.svg"
              width={80}
              height={80}
              className="w-full h-auto"
              style={{ filter: "invert(0.55) sepia(1) saturate(5) hue-rotate(185deg) brightness(1.2)" }}
              alt=""
            />
          </div>

          {/* Thumbnail framed box */}
          <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl overflow-hidden hover:scale-[1.01] transition-transform duration-300 relative group cursor-pointer" onClick={() => setIsPlaying(true)}>
            
            {/* Play Button Overlay */}
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300 z-10 rounded-[18px]" />
            <motion.div 
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm p-4 md:p-5 rounded-full shadow-2xl flex items-center justify-center cursor-pointer"
            >
              <Image 
                src={section.videoPlayIcon || "/odoo/icons/play.svg"} 
                width={40}
                height={40}
                className="w-8 h-8 md:w-10 md:h-10 ml-0.5" 
                alt="Play Video"
              />
            </motion.div>

            <Image
              src={section.videoThumbnail}
              alt="Bakery Communication Centralized in Odoo"
              width={750}
              height={420}
              className="w-full h-auto rounded-[18px] object-cover"
            />
          </div>
        </motion.div>

      </div>

      {/* Glassmorphic Lightbox Video Modal */}
      {isPlaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="absolute inset-0" onClick={() => setIsPlaying(false)} />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden w-full max-w-[850px] aspect-video relative z-10 shadow-2xl"
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full z-20 transition-colors duration-200 flex items-center justify-center shadow-lg"
              onClick={() => setIsPlaying(false)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
 
             <iframe
               src={`https://www.youtube.com/embed/${section.videoId}?autoplay=1`}
               title="YouTube video player"
               frameBorder="0"
               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
               allowFullScreen
               className="w-full h-full"
             />
           </motion.div>
         </div>
       )}
      </section>
    );
  }

export function BakeryLoyalty({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm amber backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1250px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center justify-between">
          
          {/* Left Column: Title and Description */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6 lg:col-span-6"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Targeted{" "}
              <span className="x_wd_yellow_highlight_bold_04 mx-2 inline-block">
                {section.highlightText || "loyalty"}
              </span>{" "}
              {section.title || "programs"}
            </h2>

            <p 
              className="leading-relaxed text-gray-300 font-medium max-w-lg text-xl"
              dangerouslySetInnerHTML={{ __html: section.text }}
            />
          </motion.div>

          {/* Right Column: Grid of Stars with cursive labels */}
          <div className="lg:col-span-6 w-full flex justify-center">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 justify-center text-center max-w-[550px] w-full">
              {section.items.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8, y: 15 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 shadow-lg hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <Image
                    src={section.starIcon}
                    width={65}
                    height={65}
                    className="mb-2 hover:rotate-12 transition-transform duration-300"
                    style={{ filter: "drop-shadow(0 0 10px rgba(251,191,36,0.3))" }}
                    alt=""
                  />
                  <h5 className={`${caveat.className} text-2xl font-bold text-amber-200 mb-0`}>
                    {item}
                  </h5>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
